/**
 * RestaurantSwitcher — Select con restaurantes del usuario + role badge.
 * Extraído de DashboardLayout.jsx (iter.246 · Fase 8 UX Refactor).
 *
 * Props:
 *  - restaurants: array de restaurantes del usuario.
 *  - currentRestaurant: restaurante activo.
 *  - onSelect: callback(restaurant) al elegir uno.
 *  - currentRole: rol del usuario en el restaurante actual.
 *  - collapsed: si el sidebar está colapsado (muestra solo icono).
 */
import { Link } from "react-router-dom";
import { ChefHat, Plus } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { DropdownMenuSeparator } from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { ROLE_ICONS, ROLE_LABELS, ROLE_COLORS } from "../../lib/roleMeta";

export function RestaurantSwitcher({
  restaurants,
  currentRestaurant,
  onSelect,
  currentRole,
  collapsed,
}) {
  if (!currentRestaurant) return null;

  // Collapsed sidebar → mostrar solo el icono compacto
  if (collapsed) {
    return (
      <div className="py-3 flex justify-center border-b border-border">
        <div
          className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center"
          title={currentRestaurant.name}
        >
          <ChefHat className="w-4 h-4 text-primary" />
        </div>
      </div>
    );
  }

  const RoleIcon = ROLE_ICONS[currentRole] || ROLE_ICONS.owner;
  const roleLabel = ROLE_LABELS[currentRole] || currentRole;
  const roleColor = ROLE_COLORS[currentRole] || "text-muted-foreground bg-muted/50";

  return (
    <div className="p-4 border-b border-border">
      <Select
        value={currentRestaurant.restaurant_id}
        onValueChange={(value) => {
          const restaurant = restaurants.find((r) => r.restaurant_id === value);
          if (restaurant) onSelect(restaurant);
        }}
      >
        <SelectTrigger
          className="w-full"
          data-testid="restaurant-selector"
          aria-label="Cambiar de restaurante"
        >
          <SelectValue>
            <div className="flex items-center gap-2 truncate">
              <div className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                <ChefHat className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="truncate">{currentRestaurant.name}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {restaurants.map((restaurant) => (
            <SelectItem key={restaurant.restaurant_id} value={restaurant.restaurant_id}>
              <div className="flex items-center gap-2">
                <span className="flex-1">{restaurant.name}</span>
                {restaurant.role && restaurant.role !== "owner" && (
                  <span className="text-xs text-muted-foreground ml-2">
                    {ROLE_LABELS[restaurant.role]}
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
          <DropdownMenuSeparator />
          <Link to="/onboarding" className="w-full">
            <Button variant="ghost" className="w-full justify-start text-sm">
              <Plus className="w-4 h-4 mr-2" />
              Añadir restaurante
            </Button>
          </Link>
        </SelectContent>
      </Select>

      {/* Role badge */}
      <div
        className={`mt-2 flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium w-fit ${roleColor}`}
      >
        <RoleIcon className="w-3 h-3" />
        {roleLabel}
      </div>
    </div>
  );
}
